import React, { useEffect, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFunnelDollar, faPlus, faExclamationTriangle, faTrash } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { BIZDEV_NAV, STAGE_COLORS } from './nav';

const STAGES = ['new', 'contacted', 'qualified', 'proposal', 'negotiation', 'won', 'lost'];

const Pipeline: React.FC = () => {
    const { api } = useInternalApi();
    const [leads, setLeads] = useState<any[]>([]);
    const [funnel, setFunnel] = useState<any[]>([]);
    const [winRate, setWinRate] = useState<number | null>(null);
    const [stalled, setStalled] = useState<any[]>([]);
    const [towns, setTowns] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [stageFilter, setStageFilter] = useState('');
    const [showAdd, setShowAdd] = useState(false);
    const [form, setForm] = useState({ name: '', lead_type: 'clinic', contact_name: '', contact_email: '', contact_phone: '', estimated_seats: 0, source: 'outreach', town_id: '' });
    const [viewing, setViewing] = useState<any>(null);

    useEffect(() => { document.title = 'Pipeline & Deals - Uzima Internal'; }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (stageFilter) params.set('stage', stageFilter);
            const r = await api.get(`bizdev-api/leads/?${params}`);
            if (r.data?.status === 'success') {
                setLeads(r.data.data.leads || []);
                setFunnel(r.data.data.funnel || []);
                setWinRate(r.data.data.win_rate_pct);
                setStalled(r.data.data.stalled || []);
            }
        } finally { setLoading(false); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stageFilter]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        api.get('bizdev-api/reference/').then(r => { if (r.data?.status === 'success') setTowns(r.data.data.towns || []); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submit = async () => {
        if (!form.name.trim()) {
            Swal.fire({ icon: 'warning', title: 'Name required', text: 'Give the lead a name.' });
            return;
        }
        try {
            await api.post('bizdev-api/leads/', form);
            setShowAdd(false);
            setForm({ name: '', lead_type: 'clinic', contact_name: '', contact_email: '', contact_phone: '', estimated_seats: 0, source: 'outreach', town_id: '' });
            load();
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Could not create lead.' });
        }
    };

    const changeStage = async (lead: any, stage: string) => {
        let payload: any = { stage };
        if (stage === 'lost') {
            const { value: reason } = await Swal.fire({
                title: 'Why was this lost?', input: 'text', showCancelButton: true,
                inputValidator: (v) => (!v ? 'A reason is required' : undefined),
            });
            if (!reason) return;
            payload.lost_reason = reason;
        }
        try {
            const r = await api.patch(`bizdev-api/leads/${lead.id}/`, payload);
            if (r.data?.status === 'success' && viewing?.id === lead.id) setViewing(r.data.data);
            load();
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Could not update lead.' });
        }
    };

    const removeLead = async (lead: any) => {
        const c = await Swal.fire({ title: `Remove "${lead.name}"?`, icon: 'warning', showCancelButton: true, confirmButtonColor: '#f06548' });
        if (!c.isConfirmed) return;
        try {
            await api.delete(`bizdev-api/leads/${lead.id}/`);
            setViewing(null);
            load();
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Could not remove lead.' });
        }
    };

    return (
        <InternalShell activeDashboard="business_development" subNav={BIZDEV_NAV}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faFunnelDollar} className="me-2 text-primary" />Pipeline & Deals</h2>
                    <p className="text-muted mb-0">Every prospect and where it has reached</p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowAdd(true)}><FontAwesomeIcon icon={faPlus} className="me-2" />New Lead</button>
            </div>

            {stalled.length > 0 && (
                <div className="alert alert-warning">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    <strong>{stalled.length} open lead(s) have not moved in over 21 days.</strong>
                </div>
            )}

            <div className="d-flex gap-2 mb-3 flex-wrap align-items-center">
                <select className="form-select" style={{ maxWidth: 200 }} value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}>
                    <option value="">All stages</option>
                    {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                {funnel.map(f => (
                    <span key={f.stage} className={`badge bg-${STAGE_COLORS[f.stage] || 'secondary'} p-2`}>{f.stage}: {f.count}</span>
                ))}
                {winRate !== null && <span className="badge bg-light text-dark border p-2">Win rate: {winRate}%</span>}
            </div>

            <div className="card border-0 shadow-sm">
                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                    ) : leads.length === 0 ? (
                        <div className="text-center py-5 text-muted">No leads yet.</div>
                    ) : (
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light"><tr><th>Lead</th><th>Type</th><th>Town</th><th>Seats</th><th>Stage</th><th>Days</th><th>Owner</th></tr></thead>
                            <tbody>
                                {leads.map(l => (
                                    <tr key={l.id} style={{ cursor: 'pointer' }} className={l.is_open && l.days_in_stage > 21 ? 'table-warning' : ''} onClick={() => setViewing(l)}>
                                        <td><strong>{l.name}</strong><div className="small text-muted">{l.contact_name || '—'}</div></td>
                                        <td className="small">{l.lead_type}</td>
                                        <td className="small">{l.town || '—'}</td>
                                        <td>{l.estimated_seats}</td>
                                        <td><span className={`badge bg-${STAGE_COLORS[l.stage]}`}>{l.stage}</span></td>
                                        <td>{l.days_in_stage}</td>
                                        <td className="small">{l.owner || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {showAdd && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">New Lead</h5>
                                <button type="button" className="btn-close" onClick={() => setShowAdd(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Organisation / practitioner name</label>
                                    <input className="form-control" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} />
                                </div>
                                <div className="row g-3 mb-3">
                                    <div className="col-6">
                                        <label className="form-label">Type</label>
                                        <select className="form-select" value={form.lead_type} onChange={(e) => setForm(f => ({ ...f, lead_type: e.target.value }))}>
                                            <option value="clinic">Clinic</option><option value="institution">Institution</option>
                                            <option value="practitioner">Practitioner</option><option value="partner">Service partner</option>
                                        </select>
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label">Source</label>
                                        <select className="form-select" value={form.source} onChange={(e) => setForm(f => ({ ...f, source: e.target.value }))}>
                                            <option value="outreach">Direct outreach</option><option value="inbound">Inbound enquiry</option>
                                            <option value="referral">Referral</option><option value="event">Event</option>
                                            <option value="existing_customer">Existing customer</option><option value="other">Other</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="row g-3 mb-3">
                                    <div className="col-6">
                                        <label className="form-label">Contact name</label>
                                        <input className="form-control" value={form.contact_name} onChange={(e) => setForm(f => ({ ...f, contact_name: e.target.value }))} />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label">Contact email</label>
                                        <input type="email" className="form-control" value={form.contact_email} onChange={(e) => setForm(f => ({ ...f, contact_email: e.target.value }))} />
                                    </div>
                                </div>
                                <div className="row g-3">
                                    <div className="col-6">
                                        <label className="form-label">Town</label>
                                        <select className="form-select" value={form.town_id} onChange={(e) => setForm(f => ({ ...f, town_id: e.target.value }))}>
                                            <option value="">Not set</option>
                                            {towns.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label">Estimated seats</label>
                                        <input type="number" className="form-control" value={form.estimated_seats}
                                            onChange={(e) => setForm(f => ({ ...f, estimated_seats: Number(e.target.value) }))} />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowAdd(false)}>Cancel</button>
                                <button className="btn btn-primary" onClick={submit}>Create</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {viewing && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{viewing.name}</h5>
                                <button type="button" className="btn-close" onClick={() => setViewing(null)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="d-flex gap-2 mb-3">
                                    <span className={`badge bg-${STAGE_COLORS[viewing.stage]}`}>{viewing.stage}</span>
                                    <span className="badge bg-light text-dark">{viewing.lead_type}</span>
                                    <span className="badge bg-light text-dark">{viewing.estimated_seats} seats</span>
                                    {viewing.proposals > 0 && <span className="badge bg-info">{viewing.proposals} proposal(s)</span>}
                                </div>
                                <div className="row g-2 small mb-3">
                                    <div className="col-md-6">Contact: {viewing.contact_name || '—'}</div>
                                    <div className="col-md-6">Email: {viewing.contact_email || '—'}</div>
                                    <div className="col-md-6">Phone: {viewing.contact_phone || '—'}</div>
                                    <div className="col-md-6">Town: {viewing.town || '—'}</div>
                                    <div className="col-md-6">Source: {viewing.source}</div>
                                    <div className="col-md-6">Days in stage: {viewing.days_in_stage}</div>
                                </div>
                                {viewing.lost_reason && <div className="alert alert-danger py-2 small">Lost: {viewing.lost_reason}</div>}
                                <label className="form-label small">Move to stage</label>
                                <div className="d-flex gap-1 flex-wrap">
                                    {STAGES.map(s => (
                                        <button key={s} className={`btn btn-sm ${viewing.stage === s ? `btn-${STAGE_COLORS[s]}` : 'btn-outline-secondary'}`}
                                            onClick={() => changeStage(viewing, s)} disabled={viewing.stage === s}>{s}</button>
                                    ))}
                                </div>
                            </div>
                            <div className="modal-footer">
                                {viewing.is_open && (
                                    <button className="btn btn-outline-danger me-auto" onClick={() => removeLead(viewing)}>
                                        <FontAwesomeIcon icon={faTrash} className="me-1" />Remove
                                    </button>
                                )}
                                <button className="btn btn-light" onClick={() => setViewing(null)}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </InternalShell>
    );
};

export default Pipeline;
